import React, { useEffect, useRef, useState } from 'react';
import { ShieldCheck, AlertCircle, ChevronRight, ArrowLeft, KeyRound } from 'lucide-react';

/**
 * Steg tva i inloggningen: engangskod fran autentiseringsappen.
 *
 * Ligger i egen fil for att LoginPage.js redan passerat gransen dar ny
 * funktionalitet ska brytas ut (1214 rader).
 *
 * @param {object} props
 * @param {string} props.email E-post som loggar in — visas som bekraftelse
 * @param {(kod: string, reservkod: boolean) => Promise<void>} props.onVerify
 * @param {() => void} props.onCancel Tillbaka till losenordssteget
 * @param {string} props.error Felmeddelande fran backend
 * @param {boolean} props.busy
 */
export default function MfaChallengeCard({ email, onVerify, onCancel, error, busy }) {
  const [kod, setKod] = useState('');
  const [reservlage, setReservlage] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    // Fokus direkt — anvandaren har redan appen oppen och ska bara knappa in.
    try { inputRef.current?.focus(); } catch { /* ignore */ }
  }, [reservlage]);

  const submit = (e) => {
    e.preventDefault();
    const rensad = kod.trim();
    if (!rensad) return;
    onVerify(rensad, reservlage);
  };

  const bytLage = () => {
    setReservlage((v) => !v);
    setKod('');
  };

  const klarAttSkicka = reservlage ? kod.trim().length > 0 : kod.replace(/\s/g, '').length === 6;

  return (
    <div
      className="w-[min(440px,90vw)] rounded-[12px] border border-[#E2E8F0] dark:border-gray-700 bg-white dark:bg-gray-900/95 shadow-[0_4px_20px_rgba(30,41,59,0.10)] flex flex-col"
      style={{ minHeight: 420 }}
    >
      <div className="p-6 sm:p-8 flex-1 flex flex-col justify-center">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#64748B] dark:text-gray-400 hover:text-[#1E293B] dark:hover:text-gray-200 transition-colors mb-5 self-start"
        >
          <ArrowLeft className="h-4 w-4" />
          Tillbaka
        </button>

        <div className="mb-6">
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="h-[22px] w-[22px] text-[#3B82F6] dark:text-[#F97316]" strokeWidth={2} />
            <div className="text-[22px] font-bold tracking-[-0.01em] text-[#1E293B] dark:text-gray-100">
              Tvåfaktor
            </div>
          </div>
          <p className="text-[13.5px] text-[#64748B] dark:text-gray-400 mt-1.5">
            {reservlage
              ? 'Ange en av dina reservkoder.'
              : 'Öppna din autentiseringsapp och ange den sexsiffriga koden.'}
          </p>
          {email && (
            <p className="text-[12px] text-[#8D9DB0] dark:text-gray-500 mt-1">{email}</p>
          )}
        </div>

        {error && (
          <div className="mb-4 flex items-start gap-2.5 rounded-[10px] border border-[#FECACA] dark:border-red-800/60 bg-[#FEF2F2] dark:bg-red-950/40 px-4 py-3">
            <AlertCircle className="h-4 w-4 text-[#DC2626] dark:text-[#F87171] flex-shrink-0 mt-0.5" />
            <span className="text-[13px] leading-relaxed text-[#991B1B] dark:text-[#FCA5A5]">{error}</span>
          </div>
        )}

        <form className="grid gap-4" onSubmit={submit}>
          <label className="grid gap-1.5">
            <span className="text-[12px] font-semibold text-[#1E293B] dark:text-gray-300">
              {reservlage ? 'Reservkod' : 'Engångskod'}
            </span>
            <div className="relative">
              <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8D9DB0] dark:text-gray-500" />
              <input
                ref={inputRef}
                type="text"
                inputMode={reservlage ? 'text' : 'numeric'}
                autoComplete="one-time-code"
                maxLength={reservlage ? 12 : 7}
                className="w-full pl-10 pr-3 h-[42px] rounded-[10px] border-[1.5px] border-transparent bg-[#F0F2F5] dark:bg-gray-800 dark:border-gray-700 text-[13.5px] font-medium text-[#1E293B] dark:text-gray-100 placeholder:text-[#8D9DB0] placeholder:font-normal focus:outline-none focus:border-[#3B82F6] focus:bg-white focus:shadow-[0_0_0_3px_rgba(59,130,246,0.15)] dark:focus:border-[#F97316] dark:focus:shadow-[0_0_0_3px_rgba(249,115,22,0.2)] transition-all tracking-[0.2em]"
                placeholder={reservlage ? 'abcd-efgh' : '000000'}
                value={kod}
                onChange={(e) => setKod(e.target.value)}
              />
            </div>
          </label>

          <button
            type="submit"
            disabled={busy || !klarAttSkicka}
            className="group inline-flex items-center justify-center gap-2 w-full h-[44px] rounded-[6px] bg-[#3B82F6] dark:bg-[#F97316] px-5 text-[13.5px] text-white font-semibold shadow-[0_1px_3px_rgba(59,130,246,0.25)] hover:bg-[#2563EB] hover:shadow-[0_2px_8px_rgba(59,130,246,0.3)] dark:hover:bg-[#ea580c] focus:outline-none focus:ring-4 focus:ring-[#3B82F6]/25 dark:focus:ring-[#F97316]/30 disabled:opacity-60 transition-all"
          >
            {busy ? 'Verifierar…' : 'Verifiera'}
            <ChevronRight className="h-4 w-4 opacity-90 group-hover:translate-x-0.5 transition-transform" />
          </button>

          <div className="mt-2 pt-4 border-t border-[#E2E8F0] dark:border-gray-700">
            <div className="text-[13px] text-[#475569] dark:text-gray-400 leading-relaxed">
              {reservlage
                ? 'Har du appen tillgänglig igen?'
                : 'Ingen tillgång till telefonen?'}
            </div>
            <button
              type="button"
              onClick={bytLage}
              className="mt-1.5 text-[13px] text-[#3B82F6] dark:text-[#F97316] hover:text-[#2563EB] dark:hover:text-[#fb923c] font-semibold hover:underline transition-colors"
            >
              {reservlage ? 'Använd engångskod i stället' : 'Använd en reservkod'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
